import { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box,
} from '@mui/material';
import { Button } from '../../../../shared/components/button/Button';
import { Loader } from '../../../../shared/components/Loader';
import FornecedorController from '../../../../controllers/FornecedorController';
import { formatCurrency } from '../../../../shared/utils/helpers/numbers';

interface Props {
  aberto: boolean;
  fornecedorId: number | null;
  fornecedorNome?: string;
  aoFechar: () => void;
}

/**
 * Extrato da conta corrente do FORNECEDOR.
 *
 * [05/11/2026 — scripts 265 e 266] Espelho do `ModalContaCorrente` do cliente, e de
 * propósito: duas telas iguais são uma tela que se aprende uma vez.
 *
 * **O sinal é o oposto do da conta corrente de cliente.** Positivo aqui quer dizer
 * crédito a favor da Promatec — o fornecedor nos deve, porque recebemos de volta uma
 * mercadoria que já tínhamos pago. Lá, positivo quer dizer que a Promatec deve. São duas
 * tabelas separadas por isso mesmo (script 208), e é por isso que o sinal é dito em
 * palavras abaixo: "R$ 1.536,97" sozinho não diz de quem é o dinheiro.
 *
 * Diálogo na tela de fornecedores, e não rota nova: quem quer saber o saldo de um
 * fornecedor vai procurá-lo onde ele é cadastrado. Mesma razão que valeu para o cliente
 * em 25/09.
 */
export const ModalContaCorrenteFornecedor = ({
  aberto,
  fornecedorId,
  fornecedorNome,
  aoFechar,
}: Props) => {
  const [carregando, setCarregando] = useState(false);
  const [movimentos, setMovimentos] = useState<any[]>([]);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    if (!fornecedorId) return;

    setCarregando(true);
    setErro(null);

    const resposta = await FornecedorController.ContaCorrente(fornecedorId);

    setCarregando(false);

    if (resposta.StatusCode !== 200) {
      setMovimentos([]);
      return setErro('Não foi possível carregar a conta corrente deste fornecedor.');
    }

    setMovimentos(Array.isArray(resposta.Data) ? resposta.Data : []);
  }, [fornecedorId]);

  useEffect(() => {
    if (aberto) carregar();
  }, [aberto, carregar]);

  // O saldo vem da coluna `saldo_total`, que a procedure calcula pela função de saldo —
  // a fonte única — e repete em toda linha.
  //
  // Não se usa o `saldo_acumulado` da última linha, como faz a tela do cliente: aquele é
  // a soma corrente das linhas DEVOLVIDAS, e passaria a mentir no dia em que esta tela
  // ganhar filtro de data, que a procedure já aceita. O defeito existe lá e não foi
  // copiado para cá.
  const saldo = movimentos.length > 0 ? Number(movimentos[0]?.saldo_total ?? 0) : 0;

  return (
    <Dialog open={aberto} onClose={aoFechar} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: '#D91E18', color: 'common.white' }}>
        Conta corrente de fornecedor {fornecedorNome ? `— ${fornecedorNome}` : ''}
      </DialogTitle>

      <DialogContent>
        <Loader loading={carregando}>
          <Box mt={2} mb={2}>
            <Typography variant="subtitle2" color="text.secondary">
              Saldo atual
            </Typography>
            <Typography variant="h6">
              {formatCurrency(saldo)}{' '}
              <Typography component="span" variant="body2" color="text.secondary">
                {saldo > 0.004
                  ? 'a favor da Promatec (o fornecedor deve)'
                  : saldo < -0.004
                    ? 'devido ao fornecedor'
                    : 'sem saldo'}
              </Typography>
            </Typography>
          </Box>

          {erro && (
            <Typography variant="body2" color="error" mb={2}>
              {erro}
            </Typography>
          )}

          {!erro && movimentos.length === 0 && !carregando && (
            /* Extrato vazio não é falha: a maioria dos fornecedores nunca teve devolução.
               Dizer isso evita a dúvida de "não carregou" ou "não tem". */
            <Typography variant="body2" color="text.secondary">
              Este fornecedor não tem movimento em conta corrente.
            </Typography>
          )}

          {movimentos.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Origem</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell align="right">Saldo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movimentos.map((m: any) => (
                  <TableRow key={m.fcc_id}>
                    <TableCell>
                      {m.fcc_data
                        ? String(m.fcc_data).substring(0, 10).split('-').reverse().join('/')
                        : ''}
                    </TableCell>
                    <TableCell>{m.fcc_tipo}</TableCell>
                    <TableCell>{m.fcc_descricao}</TableCell>
                    <TableCell>
                      {/* O id da origem ao lado do nome: é o que permite conferir a linha
                          contra o documento que a gerou — a nota de devolução, ou a
                          parcela de contas a pagar que consumiu o crédito. */}
                      {m.fcc_origem}
                      {m.fcc_idOrigem ? ` ${m.fcc_idOrigem}` : ''}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(Number(m.fcc_valor ?? 0))}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(Number(m.saldo_acumulado ?? 0))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Loader>
      </DialogContent>

      <DialogActions>
        <Button title="Fechar" variant="secondary" onClick={aoFechar} />
      </DialogActions>
    </Dialog>
  );
};

export default ModalContaCorrenteFornecedor;
